import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Send,
  ArrowBack,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const getEmailValidationSchema = () => yup.object({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
});

const getPasswordValidationSchema = () => yup.object({
  password: yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
});

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { requestPasswordReset, resetPassword } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [resetToken, setResetToken] = useState<string | null>(null);

  // Check for reset token on mount
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setResetToken(token);
      setStep('reset');
    }
  }, [searchParams]);

  // Email form for requesting reset
  const emailFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: getEmailValidationSchema(),
    onSubmit: async (values, { setSubmitting }) => {
      setError(null);
      setSuccess(null);
      try {
        await requestPasswordReset(values.email);
        setSuccess('Password reset link sent! Check your email for instructions.');
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to send reset link');
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Password form for resetting with token
  const passwordFormik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: getPasswordValidationSchema(),
    onSubmit: async (values, { setSubmitting }) => {
      setError(null);
      setSuccess(null);
      if (!resetToken) {
        setError('Invalid reset token');
        return;
      }
      
      try {
        await resetPassword(resetToken, values.password);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Password reset failed');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '200%',
            height: '200%',
            top: '-50%',
            left: '-50%',
            background: 'radial-gradient(circle, rgba(239, 68, 68, 0.1) 0%, transparent 70%)',
            animation: 'pulse 15s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: '200%',
            height: '200%',
            top: '-50%',
            right: '-50%',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
            animation: 'pulse 20s ease-in-out infinite reverse',
          },
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card
          sx={{
            width: { xs: 350, sm: 400 },
            p: 4,
            background: 'rgba(26, 26, 46, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <Typography
                variant="h3"
                className="gradient-text"
                sx={{ fontWeight: 700, mb: 1 }}
              >
                {t('common.appName')}
              </Typography>
            </motion.div>
            <Typography variant="h5" sx={{ mb: 1, color: 'white' }}>
              {step === 'request' ? 'Reset Password' : 'Create New Password'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step === 'request' 
                ? 'Enter your email to receive a password reset link'
                : 'Enter your new password'
              }
            </Typography>
          </Box>

          <Stepper activeStep={step === 'request' ? 0 : 1} sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Request Reset</StepLabel>
            </Step>
            <Step>
              <StepLabel>New Password</StepLabel>
            </Step>
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {step === 'request' && (
            <form onSubmit={emailFormik.handleSubmit}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                type="email"
                value={emailFormik.values.email}
                onChange={emailFormik.handleChange}
                error={emailFormik.touched.email && Boolean(emailFormik.errors.email)}
                helperText={emailFormik.touched.email && emailFormik.errors.email}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={emailFormik.isSubmitting}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #ef4444 0%, #8b5cf6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #f87171 0%, #a78bfa 100%)',
                  },
                  mb: 2
                }}
                startIcon={emailFormik.isSubmitting ? undefined : <Send />}
              >
                {emailFormik.isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          )}

          {step === 'reset' && resetToken && (
            <form onSubmit={passwordFormik.handleSubmit}>
              <TextField
                fullWidth
                id="password"
                name="password"
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={passwordFormik.values.password}
                onChange={passwordFormik.handleChange}
                error={passwordFormik.touched.password && Boolean(passwordFormik.errors.password)}
                helperText={passwordFormik.touched.password && passwordFormik.errors.password}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordFormik.values.confirmPassword}
                onChange={passwordFormik.handleChange}
                error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
                helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={passwordFormik.isSubmitting}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #ef4444 0%, #8b5cf6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #f87171 0%, #a78bfa 100%)',
                  },
                  mb: 2
                }}
              >
                {passwordFormik.isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/login')}
              sx={{ color: '#8b9eff', textTransform: 'none' }}
            >
              Back to Login
            </Button>
          </Box>
        </Card>
      </motion.div>
    </Box>
  );
};

export default ResetPassword;